import React, { useMemo } from 'react';
import { AUDIENCE_LABELS, DocAudience, DocEntry } from './useDocsManifest';

interface DocsIndexProps {
  trees: Record<DocAudience, DocEntry[]>;
  search: string;
  currentDocId?: string;
  onSelect: (entry: DocEntry, anchor?: string) => void;
}

interface Match {
  entry: DocEntry;
  headings: { text: string; anchor: string }[];
}

const matchesEntry = (entry: DocEntry, term: string): Match | null => {
  if (!term) return { entry, headings: [] };

  const needle = term.toLowerCase();
  const inTitle = entry.title.toLowerCase().includes(needle);
  const inTags = (entry.tags || []).some((t) => t.toLowerCase().includes(needle));
  const headings = (entry.headings || [])
    .filter((h) => h.text.toLowerCase().includes(needle))
    .map((h) => ({ text: h.text, anchor: h.anchor }));

  if (!inTitle && !inTags && !headings.length) return null;
  return { entry, headings: headings.slice(0, 5) };
};

const DocsIndex: React.FC<DocsIndexProps> = ({ trees, search, currentDocId, onSelect }) => {
  const sections = useMemo(() => {
    const audiences = Object.keys(trees) as DocAudience[];
    return audiences
      .map((audience) => ({
        audience,
        matches: (trees[audience] || [])
          .map((entry) => matchesEntry(entry, search.trim()))
          .filter((m): m is Match => m !== null),
      }))
      .filter((section) => section.matches.length > 0);
  }, [trees, search]);

  if (!sections.length) {
    return (
      <p className="text-sm text-gray-500 my-4">
        {search.trim()
          ? `No documentation matches “${search.trim()}”.`
          : 'No documentation is available for your account yet.'}
      </p>
    );
  }

  return (
    <nav aria-label="Documentation contents">
      {sections.map(({ audience, matches }) => (
        <section key={audience} className="mb-5">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            {AUDIENCE_LABELS[audience] || audience}
          </h3>

          <ul className="list-none p-0 m-0">
            {matches.map(({ entry, headings }) => (
              <li key={entry.id} className="mb-1">
                <button
                  type="button"
                  onClick={() => onSelect(entry)}
                  aria-current={entry.id === currentDocId ? 'page' : undefined}
                  className={`w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${
                    entry.id === currentDocId ? 'font-semibold text-link' : ''
                  }`}
                >
                  {entry.title}
                </button>

                {headings.length > 0 && (
                  <ul className="list-none pl-4 m-0">
                    {headings.map((heading) => (
                      <li key={heading.anchor}>
                        <button
                          type="button"
                          onClick={() => onSelect(entry, heading.anchor)}
                          className="w-full text-left px-2 py-0.5 rounded text-xs text-gray-600 hover:bg-gray-100"
                        >
                          {heading.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </nav>
  );
};

export default DocsIndex;
